import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import { navigate } from "gatsby";
import GlobalContext from "../../context/GlobalContext";
import { useAuth } from "../../context/AuthContext";
import { useUserPlan } from "../../context/UserPlanContext";
import { canAccessDashboard, normalizePlanDetails } from "../../utils/planUtils";
import { Select } from "../Core";
import { signUp, login, createUser } from "../../services/auth";
import {
  fetchCitiesByStates,
  fetchStateZoneMatchesForCity,
  fetchCategorySuggestions,
  fetchProductSuggestions,
  fetchDashboardUserStatus,
} from "../../services/tenders";

/** User-facing message when signup send-OTP fails (e.g. FastAPI `{ detail: "…" }`). */
function messageFromSendSignupOtpError(err) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
    if (typeof data.detail === "string" && data.detail.trim()) return data.detail.trim();
    if (Array.isArray(data.detail) && data.detail.length > 0) {
      const first = data.detail[0];
      if (typeof first === "string") return first;
      if (first && typeof first === "object" && typeof first.msg === "string") {
        return first.msg.trim();
      }
    }
    if (Array.isArray(data.non_field_errors) && data.non_field_errors[0]) {
      return String(data.non_field_errors[0]).trim();
    }
  }
  const raw = String(err?.message || "").trim();
  if (raw && !/^request failed with status code \d+/i.test(raw)) return raw;
  return "Failed to send OTP. Please try again.";
}

const ModalStyled = styled(Modal)`
  .modal-dialog {
    max-width: ${props => props.$isSignUp ? '900px' : '420px'};
    transition: max-width 0.3s ease;
  }
  .modal-content {
    border: none;
    border-radius: 6px;
    overflow: visible;
    background: transparent;
  }
`;

const FormContainer = styled.div`
  background: #ffffff;
  border-radius: 6px;
  overflow: visible;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const FormHeader = styled.div`
  background: #00b074;
  padding: 1rem 1.5rem;
  text-align: center;

  h3 {
    color: white;
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
    margin: 0;
  }
`;

const FormBody = styled.div`
  padding: 1.25rem 1.5rem;
  background: #ffffff;
  overflow: visible;

  .form-group {
    margin-bottom: 0.875rem;
    overflow: visible;
  }

  label {
    color: #333333;
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.3rem;
    display: block;
  }

  .form-control {
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 3px;
    color: #333333;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &::placeholder {
      color: #999999;
    }

    &:focus {
      background: #ffffff;
      border-color: #00b074;
      box-shadow: 0 0 0 2px rgba(0, 176, 116, 0.15);
      outline: none;
    }
  }

  textarea.form-control {
    min-height: 60px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.625rem;
  background: #00b074;
  border: none;
  border-radius: 3px;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #009963;
    transform: translateY(-1px);
    box-shadow: 0 6px 15px rgba(0, 176, 116, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: 0.85rem;

  a {
    color: #00b074;
    text-decoration: none;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  span {
    color: #666666;
  }
`;

const ForgotLink = styled.a`
  color: #00b074 !important;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  margin-top: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    background: white;

    i {
      color: #00b074;
    }
  }

  i {
    color: #333333;
    font-size: 0.85rem;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0;
  }
`;

const Row3Col = styled.div`
  display: flex;
  gap: 0.75rem;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0;
  }
`;

const HalfCol = styled.div`
  flex: 1;
`;

const ThirdCol = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const RequiredStar = styled.span`
  color: #e53935;
  margin-left: 2px;
  font-weight: 700;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const PasswordTooltip = styled.div`
  position: fixed;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  width: 320px;
  max-width: 90vw;
  font-size: 0.8rem;
  line-height: 1.6;
  white-space: normal;
  pointer-events: auto;

  h6 {
    margin: 0 0 10px 0;
    font-size: 0.85rem;
    font-weight: 700;
    color: #333;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    color: #666;
    list-style-position: outside;
  }

  li {
    margin-bottom: 6px;
    padding-left: 4px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 30px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #ffffff;
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 30px;
    width: 0;
    height: 0;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 9px solid #e0e0e0;
    margin-top: 1px;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;

  .password-toggle {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 4px 8px;
    z-index: 10;
    font-size: 0.9rem;

    &:hover {
      color: #00b074;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #e53935;
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SuccessMessage = styled.div`
  color: #00b074;
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e7f3ff;
  color: #00b074;
  font-size: 0.7rem;
  font-weight: 700;
  cursor: help;
  margin-left: 6px;
  vertical-align: middle;
  position: relative;
  transition: all 0.2s ease;
  z-index: 999;
  &:hover {
    background: #00b074;
    color: #ffffff;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;

  .step {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;

    .step-number {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.9rem;
    }
  }

  .step-divider {
    width: 40px;
    height: 2px;
  }
`;

const RequirementsInputWrapper = styled.div`
  position: relative;
  width: 100%;

  .suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 3px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-height: 200px;
    overflow-y: auto;
    margin-top: 2px;

    .suggestion-item {
      padding: 0.5rem 0.75rem;
      cursor: pointer;
      font-size: 0.9rem;
      color: #333;
      transition: background 0.2s ease;

      &:hover {
        background: #f8f9fa;
      }

      &:first-child {
        border-radius: 3px 3px 0 0;
      }

      &:last-child {
        border-radius: 0 0 3px 3px;
      }
    }
  }
`;

const signupStep2LocationSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "48px",
    height: "48px",
    width: "100%",
    border: state.isFocused ? "1px solid #00b074" : "1px solid #e0e0e0",
    borderRadius: "3px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 176, 116, 0.15)" : "none",
    padding: "0",
    backgroundColor: state.isFocused ? "#ffffff" : "#f8f9fa",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      border: state.isFocused ? "1px solid #00b074" : "1px solid #e0e0e0",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0.5rem 0.75rem",
    minHeight: "48px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
    padding: "0",
    height: "auto",
    fontSize: "0.9rem",
    lineHeight: "1.5",
    minWidth: "0.5em",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "48px",
    minHeight: "48px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "4px 8px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#999999",
    margin: "0",
    fontSize: "0.9rem",
  }),
  singleValue: (provided) => ({
    ...provided,
    margin: "0",
    color: "#333333",
    fontSize: "0.9rem",
    lineHeight: "1.5",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const StepNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  gap: 1rem;

  button {
    flex: 1;
  }
`;

const ModalAuth = (props) => {
  const [showPass, setShowPass] = useState(true);
  const [showConfirmPass, setShowConfirmPass] = useState(true);
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [tooltipTimeout, setTooltipTimeout] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const infoIconRef = useRef(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [signupStep, setSignupStep] = useState(1); // 1 or 2
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [cityLocationMatches, setCityLocationMatches] = useState([]);
  const [cityLocationLoading, setCityLocationLoading] = useState(false);
  const locationFetchIdRef = useRef(0);
  const [requirementsSuggestions, setRequirementsSuggestions] = useState([]);
  const [showRequirementsSuggestions, setShowRequirementsSuggestions] = useState(false);
  const [requirementsInputValue, setRequirementsInputValue] = useState("");
  const [userId, setUserId] = useState(null); // Store user ID from Step 1 response
  const [otpSessionId, setOtpSessionId] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSuccessMessage, setOtpSuccessMessage] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    mobile: "",
    otp: "",
    companyName: "",
    contactName: "",
    city: "",
    state: "",
    zone: "",
    requirements: "",
  });
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpFieldError, setOtpFieldError] = useState("");
  const [sendOtpMobileError, setSendOtpMobileError] = useState("");

  const gContext = useContext(GlobalContext);
  const auth = useAuth();
  const { sendSignupOtp, resendSignupOtp, verifySignupOtp } = auth;
  const { setPlanDetails } = useUserPlan();

  /** Clears form, OTP flow, step-2 draft state — used on close, fresh signup, and login→signup. */
  const resetInternalAuthState = useCallback(() => {
    setError("");
    setPasswordErrors([]);
    setShowPasswordTooltip(false);
    setTooltipTimeout((timeoutId) => {
      if (timeoutId) clearTimeout(timeoutId);
      return null;
    });
    setCityLocationMatches([]);
    setCityLocationLoading(false);
    locationFetchIdRef.current += 1;
    setRequirementsSuggestions([]);
    setShowRequirementsSuggestions(false);
    setRequirementsInputValue("");
    setUserId(null);
    setOtpSessionId(null);
    setOtpSent(false);
    setOtpVerified(false);
    setOtpSuccessMessage("");
    setSendingOtp(false);
    setVerifyingOtp(false);
    setOtpFieldError("");
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      mobile: "",
      otp: "",
      companyName: "",
      contactName: "",
      city: "",
      state: "",
      zone: "",
      requirements: "",
    });
  }, []);

  // When modal opens with "open as signup", show signup form first
  useEffect(() => {
    if (gContext.authModalVisible && gContext.authModalOpenAsSignUp) {
      resetInternalAuthState();
      setIsSignUp(true);
      setSignupStep(1);
      if (typeof gContext.setAuthModalOpenAsSignUp === "function") {
        gContext.setAuthModalOpenAsSignUp(false);
      }
    }
  }, [gContext.authModalVisible, gContext.authModalOpenAsSignUp, resetInternalAuthState]);

  // City list for signup step 2 (all cities)
  useEffect(() => {
    const loadCities = async () => {
      try {
        const allCities = await fetchCitiesByStates([]);
        setCitiesOptions(allCities || []);
      } catch (err) {
        console.error("Error loading cities:", err);
      }
    };
    loadCities();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
      }
    };
  }, [tooltipTimeout]);

  // Update tooltip position on scroll/resize
  useEffect(() => {
    if (showPasswordTooltip && infoIconRef.current) {
      const updatePosition = () => {
        if (infoIconRef.current) {
          const rect = infoIconRef.current.getBoundingClientRect();
          setTooltipPosition({
            top: rect.top - 8,
            left: rect.left - 160,
          });
        }
      };

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showPasswordTooltip]);

  // Step 2 cannot be skipped by closing the modal
  const isSignupStep2Locked = isSignUp && signupStep === 2;

  const handleClose = () => {
    if (isSignupStep2Locked) {
      setSignupStep(2);
      setError("Please complete City, State, Zone, and Requirements — this step cannot be skipped.");
      return;
    }
    gContext.toggleAuthModal();
    if (typeof gContext.setAuthModalOpenAsSignUp === "function") {
      gContext.setAuthModalOpenAsSignUp(false);
    }
    setIsSignUp(false);
    setSignupStep(1);
    resetInternalAuthState();
  };

  const handleTooltipEnter = () => {
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }

    // Calculate tooltip position based on icon position
    if (infoIconRef.current) {
      const rect = infoIconRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 8, // Position above the icon
        left: rect.left - 160, // Center it relative to icon (tooltip width is ~320px)
      });
    }

    setShowPasswordTooltip(true);
  };

  const handleTooltipLeave = () => {
    const timeout = setTimeout(() => {
      setShowPasswordTooltip(false);
    }, 100); // Small delay to prevent flickering
    setTooltipTimeout(timeout);
  };

  const togglePassword = () => {
    setShowPass(!showPass);
  };

  const toggleConfirmPassword = () => {
    setShowConfirmPass(!showConfirmPass);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For mobile number, remove spaces automatically
    let processedValue = value;
    if (name === "mobile") {
      processedValue = value.replace(/\s/g, "");
    }
    if (name === "otp") {
      processedValue = value.replace(/\D/g, "").slice(0, 6);
    }
    if (name === "mobile") {
      setFormData({ ...formData, mobile: processedValue, otp: "" });
      setOtpSent(false);
      setOtpSessionId(null);
      setOtpVerified(false);
      setOtpSuccessMessage("");
      setOtpFieldError("");
      setSendOtpMobileError("");
      setVerifyingOtp(false);
      setSendingOtp(false);
    } else {
      setFormData({ ...formData, [name]: processedValue });
    }
    if (name === "otp") {
      setOtpFieldError("");
      if (otpVerified || otpSuccessMessage) {
        setOtpVerified(false);
        setOtpSuccessMessage("");
      }
    }

    // Validate password in real-time (only if user has started typing)
    if (name === "password" && value.length > 0) {
      validatePassword(value);
    } else if (name === "password" && value.length === 0) {
      setPasswordErrors([]);
    }

    // Clear errors when user types
    if (error) {
      setError("");
    }
  };

  // Password validation rules
  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    if (password.length > 20) {
      errors.push("Password should not exceed 20 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least one uppercase letter (A-Z) required");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least one lowercase letter (a-z) required");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least one number (0-9) required");
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("At least one special character required");
    }
    if (/\s/.test(password)) {
      errors.push("Password cannot contain spaces");
    }
    if (/123456|password|qwerty/i.test(password)) {
      errors.push("Avoid common passwords like 123456, password, or qwerty");
    }

    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const validateForm = () => {
    if (isSignUp) {
      // Sign up validation - trim all fields
      const email = formData.email?.trim() || "";
      const password = formData.password?.trim() || "";
      const confirmPassword = formData.confirmPassword?.trim() || "";
      const mobile = formData.mobile?.trim() || "";
      const companyName = formData.companyName?.trim() || "";
      const contactName = formData.contactName?.trim() || "";
      const state = formData.state?.trim() || "";
      const city = formData.city?.trim() || "";
      const zone = formData.zone?.trim() || "";
      const requirements = formData.requirements?.trim() || "";

      if (!email || !password || !confirmPassword || !mobile || !companyName ||
          !contactName || !state || !city || !zone || !requirements) {
        setError("Please fill in all required fields");
        return false;
      }

      // Validate password rules
      const passwordValid = validatePassword(password);
      if (!passwordValid) {
        setError("Password does not meet the requirements. Please check the password rules.");
        return false;
      }

      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return false;
      }
    } else {
      // Login validation - trim fields
      const email = formData.email?.trim() || "";
      const password = formData.password?.trim() || "";

      if (!email || !password) {
        setError("Please enter email and password");
        return false;
      }
    }

    return true;
  };

  // Validate Step 1 fields (includes Password and Confirm Password)
  const validateStep1 = () => {
    const companyName = formData.companyName?.trim() || "";
    const contactName = formData.contactName?.trim() || "";
    const mobile = formData.mobile?.trim() || "";
    const email = formData.email?.trim() || "";
    const password = formData.password?.trim() || "";
    const confirmPassword = formData.confirmPassword?.trim() || "";

    if (!companyName || !contactName || !mobile || !email || !password || !confirmPassword) {
      setError("Please fill in all required fields: Company Name, Contact Name, Mobile Number, Email ID, Password, and Confirm Password");
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    // Basic mobile validation (10 digits, no spaces)
    const mobileDigits = mobile.replace(/\s/g, "");
    if (!/^\d{10}$/.test(mobileDigits)) {
      setError("Please enter a valid 10-digit mobile number");
      return false;
    }
    if (!otpVerified) {
      setOtpFieldError("");
      if (!otpSent) {
        setError(
          "Please tap Send OTP to receive a code on your mobile, then enter it and tap Verify OTP before starting tender access."
        );
      } else {
        const otpTrim = (formData.otp || "").trim();
        if (!otpTrim) {
          setError(
            "Please enter the OTP sent to your mobile and tap Verify OTP before starting tender access."
          );
        } else {
          setError(
            "Your mobile number is not verified yet. Tap Verify OTP, or correct the OTP if the code was wrong."
          );
        }
      }
      return false;
    }
    // Validate password rules
    const passwordValid = validatePassword(password);
    if (!passwordValid) {
      setError("Password does not meet the requirements. Please check the password rules.");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  // Step 2 is compulsory — City, State, Zone, Requirements must all be filled (no skip)
  const isStep2Complete = () => {
    const city = formData.city?.trim() || "";
    const state = formData.state?.trim() || "";
    const zone = formData.zone?.trim() || "";
    const requirements =
      (formData.requirements?.trim() || requirementsInputValue?.trim() || "");
    return Boolean(city && state && zone && requirements) && !cityLocationLoading;
  };

  // Validate Step 2 fields (City, State, Zone, Requirements; Password/Confirm are in Step 1)
  const validateStep2 = () => {
    const state = formData.state?.trim() || "";
    const city = formData.city?.trim() || "";
    const zone = formData.zone?.trim() || "";
    const requirements =
      (formData.requirements?.trim() || requirementsInputValue?.trim() || "");

    if (cityLocationLoading) {
      setError("Please wait — loading state and zone for your city.");
      return false;
    }

    if (!city || !requirements) {
      setError("Please fill in all required fields: City, State, Zone, and Requirements");
      return false;
    }
    if (city && !cityLocationLoading && cityLocationMatches.length === 0 && !state) {
      setError("No state or zone was found for the selected city. Please choose a different city.");
      return false;
    }
    if (!state || !zone) {
      setError("Please fill in all required fields: City, State, Zone, and Requirements");
      return false;
    }
    return true;
  };

  const handleSignupSendOtp = async (isResend) => {
    const mobile = formData.mobile?.trim().replace(/\s/g, "") || "";
    if (!/^\d{10}$/.test(mobile)) {
      setSendOtpMobileError("Please enter a valid 10-digit mobile number");
      setError("");
      return;
    }
    try {
      setSendingOtp(true);
      setError("");
      setSendOtpMobileError("");
      setOtpFieldError("");
      setOtpVerified(false);
      setOtpSuccessMessage("");
      if (isResend) {
        setFormData((prev) => ({ ...prev, otp: "" }));
      }
      const sendFn = isResend ? resendSignupOtp : sendSignupOtp;
      const result = await sendFn(mobile);
      if (result.requiresOtp) {
        setOtpSent(true);
        if (result.sessionId) {
          setOtpSessionId(result.sessionId);
        }
      } else {
        setOtpSent(false);
        setOtpSessionId(null);
        setOtpVerified(false);
      }
    } catch (err) {
      setSendOtpMobileError(messageFromSendSignupOtpError(err));
      setError("");
      setOtpSent(false);
      setOtpSessionId(null);
      setOtpVerified(false);
      setOtpSuccessMessage("");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSignupVerifyOtp = async () => {
    if (otpVerified) return;
    const otp = (formData.otp || "").trim();
    if (!otp) {
      setOtpFieldError("Please enter the OTP to verify.");
      return;
    }
    if (!otpSessionId) {
      setOtpFieldError("OTP session expired or missing. Please click Send OTP again.");
      return;
    }
    try {
      setVerifyingOtp(true);
      setOtpFieldError("");
      setError("");
      const result = await verifySignupOtp(otpSessionId, otp);
      if (result.success) {
        setOtpFieldError("");
        setOtpVerified(true);
        setOtpSuccessMessage("OTP verified successfully.");
      } else {
        const message = result.message || "OTP verification failed. Please try again.";
        setOtpFieldError(message);
        setOtpVerified(false);
        setOtpSuccessMessage("");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to verify OTP. Please try again.";
      setOtpFieldError(message);
      setOtpVerified(false);
      setOtpSuccessMessage("");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const stateOptionsFromCityMatches = useMemo(() => {
    const states = [...new Set(cityLocationMatches.map((m) => m.state))];
    return states.sort().map((s) => ({ value: s, label: s }));
  }, [cityLocationMatches]);

  const zoneOptionsForSelectedState = useMemo(() => {
    if (!formData.state || !cityLocationMatches.length) return [];
    const zones = [
      ...new Set(
        cityLocationMatches.filter((m) => m.state === formData.state).map((m) => m.zone),
      ),
    ];
    return zones.sort().map((z) => ({ value: z, label: z }));
  }, [formData.state, cityLocationMatches]);

  const resolveLocationForCity = useCallback(async (cityLabel) => {
    const city = String(cityLabel || "").trim();
    const fetchId = ++locationFetchIdRef.current;
    if (!city) {
      setCityLocationMatches([]);
      setCityLocationLoading(false);
      setFormData((prev) => ({ ...prev, city: "", state: "", zone: "" }));
      return;
    }
    setCityLocationLoading(true);
    setError("");
    setFormData((prev) => ({ ...prev, city, state: "", zone: "" }));
    try {
      const { matches } = await fetchStateZoneMatchesForCity(city);
      if (fetchId !== locationFetchIdRef.current) return;
      setCityLocationMatches(matches);
      if (matches.length === 1) {
        setFormData((prev) => ({
          ...prev,
          city,
          state: matches[0].state,
          zone: matches[0].zone,
        }));
      }
    } catch (err) {
      if (fetchId !== locationFetchIdRef.current) return;
      setCityLocationMatches([]);
      setFormData((prev) => ({ ...prev, city, state: "", zone: "" }));
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not load state and zone for this city.";
      setError(typeof message === "string" ? message : "Could not load state and zone for this city.");
    } finally {
      if (fetchId === locationFetchIdRef.current) {
        setCityLocationLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isSignUp || signupStep !== 2) return;
    const c = formData.city?.trim();
    if (!c) return;
    resolveLocationForCity(c);
  }, [isSignUp, signupStep, resolveLocationForCity]);

  // Handle next step
  const handleNextStep = (e) => {
    e.preventDefault();
    setError("");
    if (validateStep1()) {
      setSignupStep(2);
    }
  };

  // Handle requirements input with autosuggestion
  const handleRequirementsChange = (e) => {
    const value = e.target.value;
    setRequirementsInputValue(value);
    setFormData({ ...formData, requirements: value });
    setShowRequirementsSuggestions(true);

    // Clear errors when user types
    if (error) {
      setError("");
    }
  };

  // Fetch autosuggestions for requirements
  useEffect(() => {
    if (!isSignUp || signupStep !== 2) return;

    const fetchSuggestions = async () => {
      if (requirementsInputValue.trim().length < 2) {
        setRequirementsSuggestions([]);
        return;
      }

      try {
        // Try category suggestions first, then product suggestions
        const [categorySuggestions, productSuggestions] = await Promise.all([
          fetchCategorySuggestions(requirementsInputValue.trim(), 10),
          fetchProductSuggestions(requirementsInputValue.trim(), 10),
        ]);

        // Combine and deduplicate suggestions
        const combined = [
          ...(categorySuggestions || []).map(s => typeof s === 'string' ? s : s.label || s.value || s),
          ...(productSuggestions || []).map(s => typeof s === 'string' ? s : s.label || s.value || s),
        ];

        const unique = [...new Set(combined)];
        setRequirementsSuggestions(unique.slice(0, 10));
      } catch (err) {
        console.error("Error fetching requirements suggestions:", err);
        setRequirementsSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [requirementsInputValue, isSignUp, signupStep]);

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    setFormData({ ...formData, requirements: suggestion });
    setRequirementsInputValue(suggestion);
    setShowRequirementsSuggestions(false);
    setRequirementsSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isSignUp && signupStep === 1) {
      // Step 1: Validate only, then move to step 2 (POST happens on Sign Up)
      if (!validateStep1()) return;
      setSignupStep(2);
      return;
    }

    if (isSignUp && signupStep === 2) {
      // Step 2 Sign Up: POST /users/ with full payload
      if (!validateStep2()) {
        return;
      }

      setIsSubmitting(true);

      try {
        const signupPayload = {
          uname: formData.contactName?.trim() || "",
          companyName: formData.companyName?.trim() || "",
          email: formData.email?.trim() || "",
          password: formData.password?.trim() || "",
          mobile: formData.mobile?.trim().replace(/\s/g, "") || "",
          zone: formData.zone?.trim() || "",
          state: formData.state?.trim() || "",
          city: formData.city?.trim() || "",
          requirements:
            formData.requirements?.trim() || requirementsInputValue?.trim() || "",
        };

        const response = await createUser(signupPayload);
        console.log("Create user successful:", response);

        // Reset form and switch to login modal
        setFormData({
          email: formData.email, // Keep email for login
          password: "",
          confirmPassword: "",
          mobile: "",
          otp: "",
          companyName: "",
          contactName: "",
          city: "",
          state: "",
          zone: "",
          requirements: "",
        });
        setError("");
        setPasswordErrors([]);
        setSignupStep(1);
        setUserId(null);
        setCityLocationMatches([]);
        setCityLocationLoading(false);
        setOtpSessionId(null);
        setOtpSent(false);
        setOtpVerified(false);
        setOtpSuccessMessage("");
        setRequirementsSuggestions([]);
        setShowRequirementsSuggestions(false);
        setRequirementsInputValue("");
        setIsSignUp(false); // Switch to login view
      } catch (err) {
        console.error("Create user error:", err);
        setError(err?.message || "Failed to create account. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!isSignUp) {
      // Login validation
      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        // Login API call
        const credentials = {
          identifier: formData.email, // Can be email or contact number
          password: formData.password,
        };

        const response = await login(credentials);
        console.log("Login successful:", response);

        // Build user from API response: { message, uid, uname, companyname, phone, plans }
        const resolvedUserId =
          (response.user && (response.user.id || response.user.user_id)) ||
          response.id ||
          response.uid ||
          null;

        const loginPlansRaw = response.plans ?? response.user?.plans;
        const loginPlans = Array.isArray(loginPlansRaw) ? loginPlansRaw : [];

        const userData = {
          ...(response.user || {}),
          id: resolvedUserId ?? response.uid ?? response.user?.id ?? response.user?.user_id,
          uid: response.uid ?? resolvedUserId,
          uname: response.uname ?? response.user?.uname,
          companyname: response.companyname ?? response.user?.companyname,
          phone: response.phone ?? response.user?.phone,
          plans: loginPlans,
          loginPlans,
          selectedstates: response.selectedstates ?? response.user?.selectedstates ?? null,
          selectedcategories: response.selectedcategories ?? response.user?.selectedcategories ?? null,
        };

        const authToken =
          response.token || response.access_token || response.accessToken;

        auth.login(userData, authToken);

        if (response.plans && response.plans.length > 0) {
          setPlanDetails(response);
        }

        const userId = userData.id ?? userData.uid ?? resolvedUserId;
        let dashboardUserStatus = null;
        if (userId != null) {
          try {
            dashboardUserStatus = await fetchDashboardUserStatus(userId);
          } catch (err) {
            console.error("Dashboard user status (post-login):", err);
          }
        }
        const hasPendingActivationForm = dashboardUserStatus?.status === "activation_required";
        const shouldAvoidDashboard = ["activation_required", "offline_payment_pending", "pending_query_creation"].includes(dashboardUserStatus?.status);

        handleClose();
        const hasPlans = Array.isArray(response.plans) && response.plans.length > 0;
        const { plans: normalizedPlans } = normalizePlanDetails(response) || {};
        const planTypes = (normalizedPlans || []).map((p) => p.plan_type).filter(Boolean);
        const allowDashboard = planTypes.length === 0 || canAccessDashboard(planTypes);
        if (shouldAvoidDashboard) {
          navigate("/");
        } else if (hasPlans && allowDashboard) {
          navigate("/dashboard");
        } else if (hasPlans && !allowDashboard) {
          navigate("/pricing?message=no-dashboard-access");
        } else {
          navigate("/pricing");
        }

        if (hasPendingActivationForm && gContext.openActivationForm) {
          gContext.openActivationForm();
        }
      } catch (err) {
        console.error("Auth error:", err);
        setError(err.message || "An error occurred. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const switchToSignUp = (e) => {
    e.preventDefault();
    resetInternalAuthState();
    setIsSignUp(true);
    setSignupStep(1);
  };

  const switchToLogin = (e) => {
    e.preventDefault();
    setIsSignUp(false);
    setSignupStep(1);
  };

  return (
    <ModalStyled
      {...props}
      centered
      show={gContext.authModalVisible}
      onHide={handleClose}
      backdrop="static"
      keyboard={!isSignupStep2Locked}
      $isSignUp={isSignUp}
    >
      <Modal.Body className="p-0">
        <FormContainer className="position-relative">
          {!isSignupStep2Locked && (
            <CloseButton type="button" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </CloseButton>
          )}
          {!isSignUp ? (
            // Login Form
            <>
              <FormHeader>
                <h3>Welcome Back!</h3>
                <p>Login to Access Your Tender Dashboard</p>
              </FormHeader>
              <FormBody>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="login-email">Email ID<RequiredStar>*</RequiredStar></label>
                    <input
                      type="text"
                      className="form-control"
                      id="login-email"
                      name="email"
                      placeholder="Enter your email or contact number"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="login-password">Password<RequiredStar>*</RequiredStar></label>
                    <PasswordInputWrapper>
                      <input
                        type={showPass ? "password" : "text"}
                        className="form-control"
                        id="login-password"
                        name="password"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        style={{ paddingRight: "40px" }}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={togglePassword}
                        tabIndex="-1"
                      >
                        <i className={showPass ? "fas fa-eye" : "fas fa-eye-slash"}></i>
                      </button>
                    </PasswordInputWrapper>
                    <ForgotLink
                      href="/forgot-password"
                      onClick={(e) => {
                        e.preventDefault();
                        handleClose();
                        navigate("/forgot-password");
                      }}
                    >
                      Forgot Password?
                    </ForgotLink>
                  </div>

                  {error && <ErrorMessage><i className="fas fa-exclamation-circle"></i> {error}</ErrorMessage>}

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </SubmitButton>

                  <LinkText>
                    <span>Don't have an account? </span>
                    <a onClick={switchToSignUp} className="px-2">
                      Sign Up
                    </a>
                  </LinkText>
                </form>
              </FormBody>
            </>
          ) : (
            // Sign Up Form
            <>
              <FormHeader>
                <h3>Start Your 100% Free Tender Access In 30 Seconds</h3>
                <p>
                  <span style={{ marginRight: '1.5rem' }}>NO PAYMENT REQUIRED</span>
                  <span style={{ marginRight: '1.5rem' }}>NO CREDIT CARD</span>
                  <span>TRUSTED BY MSME's & CONTRACTORS</span>
                </p>
              </FormHeader>
              <FormBody>
                <StepIndicator>
                  <div className="step" style={{ color: signupStep === 1 ? '#00b074' : '#999' }}>
                    <div className="step-number" style={{
                      background: signupStep === 1 ? '#00b074' : '#e0e0e0',
                      color: signupStep === 1 ? '#ffffff' : '#999'
                    }}>1</div>
                    <span>Step 1</span>
                  </div>
                  <div className="step-divider" style={{
                    background: signupStep === 2 ? '#00b074' : '#e0e0e0'
                  }}></div>
                  <div className="step" style={{ color: signupStep === 2 ? '#00b074' : '#999' }}>
                    <div className="step-number" style={{
                      background: signupStep === 2 ? '#00b074' : '#e0e0e0',
                      color: signupStep === 2 ? '#ffffff' : '#999'
                    }}>2</div>
                    <span>Step 2</span>
                  </div>
                </StepIndicator>

                <form onSubmit={handleSubmit}>
                  {signupStep === 1 ? (
                    // Step 1: Company Name, Mobile Number, Email ID
                    <>
                      <div className="form-group">
                        <Row>
                          <HalfCol>
                            <label htmlFor="signup-company">Company Name<RequiredStar>*</RequiredStar></label>
                            <input
                              type="text"
                              className="form-control"
                              id="signup-company"
                              name="companyName"
                              placeholder="Enter company name"
                              value={formData.companyName}
                              onChange={handleChange}
                              onBlur={(e) => {
                                const trimmed = e.target.value.trim();
                                if (trimmed !== e.target.value) {
                                  setFormData({ ...formData, companyName: trimmed });
                                }
                              }}
                              required
                            />
                          </HalfCol>
                          <HalfCol>
                            <label htmlFor="signup-contact">Contact Name<RequiredStar>*</RequiredStar></label>
                            <input
                              type="text"
                              className="form-control"
                              id="signup-contact"
                              name="contactName"
                              placeholder="Enter contact name"
                              value={formData.contactName}
                              onChange={handleChange}
                              required
                            />
                          </HalfCol>
                        </Row>
                      </div>


                      <div className="form-group">
                        <label htmlFor="signup-mobile">Mobile Number<RequiredStar>*</RequiredStar></label>
                        <Row>
                          <HalfCol>
                            <input
                              type="tel"
                              className={`form-control ${sendOtpMobileError ? "is-invalid" : ""}`}
                              id="signup-mobile"
                              name="mobile"
                              placeholder="10 digit mobile number"
                              value={formData.mobile}
                              onChange={handleChange}
                              aria-invalid={sendOtpMobileError ? "true" : "false"}
                              aria-describedby={sendOtpMobileError ? "signup-mobile-send-otp-err" : undefined}
                              onBlur={(e) => {
                                const trimmed = e.target.value.trim().replace(/\s/g, "");
                                if (trimmed !== formData.mobile) {
                                  setFormData({ ...formData, mobile: trimmed, otp: "" });
                                  setOtpSent(false);
                                  setOtpSessionId(null);
                                  setOtpVerified(false);
                                  setOtpSuccessMessage("");
                                  setOtpFieldError("");
                                  setSendOtpMobileError("");
                                  setVerifyingOtp(false);
                                  setSendingOtp(false);
                                } else if (trimmed !== e.target.value) {
                                  setFormData({ ...formData, mobile: trimmed });
                                }
                              }}
                              maxLength="10"
                              required
                            />
                          </HalfCol>
                          <div className="flex-shrink-0 d-flex align-items-stretch">
                            <SubmitButton
                              type="button"
                              onClick={() => handleSignupSendOtp(false)}
                              disabled={sendingOtp || otpVerified}
                            >
                              {sendingOtp ? "Sending…" : "SEND OTP"}
                            </SubmitButton>
                          </div>
                          <HalfCol>
                            <div className="d-flex">
                              {otpVerified ? (
                                <div className="input-group flex-grow-1">
                                  <input
                                    type="text"
                                    className="form-control border-success rounded-sm"
                                    id="signup-otp"
                                    name="otp"
                                    placeholder="Enter OTP"
                                    value={formData.otp}
                                    readOnly
                                    disabled
                                    maxLength="6"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    aria-invalid="false"
                                  />
                                  <div className="input-group-append">
                                    <span
                                      className="input-group-text bg-white text-success border-success"
                                      aria-hidden="true"
                                    >
                                      <i className="fas fa-check-circle" />
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  className={`form-control flex-grow-1 ${otpFieldError ? "is-invalid" : ""}`}
                                  id="signup-otp"
                                  name="otp"
                                  placeholder="Enter OTP"
                                  value={formData.otp}
                                  onChange={handleChange}
                                  maxLength="6"
                                  inputMode="numeric"
                                  autoComplete="one-time-code"
                                  disabled={!otpSent}
                                  aria-invalid={otpFieldError ? "true" : "false"}
                                  aria-describedby={otpFieldError ? "signup-otp-err" : undefined}
                                />
                              )}
                              <div className="flex-shrink-0 d-flex align-items-stretch ml-3">
                                <SubmitButton
                                  type="button"
                                  onClick={handleSignupVerifyOtp}
                                  disabled={
                                    verifyingOtp ||
                                    otpVerified ||
                                    !otpSent ||
                                    !formData.otp ||
                                    formData.otp.length < 4
                                  }
                                >
                                  {verifyingOtp ? "Verifying…" : otpVerified ? "Verified" : "VERIFY OTP"}
                                </SubmitButton>
                              </div>
                            </div>
                          </HalfCol>
                        </Row>
                        {sendOtpMobileError ? (
                          <ErrorMessage id="signup-mobile-send-otp-err" role="alert">
                            <i className="fas fa-exclamation-circle" />
                            <span>{sendOtpMobileError}</span>
                          </ErrorMessage>
                        ) : null}
                        {otpFieldError ? (
                          <ErrorMessage id="signup-otp-err" role="alert">
                            <i className="fas fa-exclamation-circle" />
                            <span>{otpFieldError}</span>
                          </ErrorMessage>
                        ) : null}
                        {otpSent && !otpVerified ? (
                          <div className="mt-2">
                            <span
                              role="button"
                              tabIndex={sendingOtp ? -1 : 0}
                              className={`font-size-small font-weight-semibold ${sendingOtp ? "text-muted" : "text-primary cursor-pointer"}`}
                              onClick={() => {
                                if (!sendingOtp) handleSignupSendOtp(true);
                              }}
                              onKeyDown={(e) => {
                                if (sendingOtp) return;
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleSignupSendOtp(true);
                                }
                              }}
                            >
                              Resend OTP
                            </span>
                          </div>
                        ) : null}
                        {otpSuccessMessage ? (
                          <SuccessMessage role="status">
                            <i className="fas fa-check-circle" />
                            <span>{otpSuccessMessage}</span>
                          </SuccessMessage>
                        ) : null}
                      </div>

                      <div className="form-group">
                        <label htmlFor="signup-email">Email ID<RequiredStar>*</RequiredStar></label>
                        <input
                          type="email"
                          className="form-control"
                          id="signup-email"
                          name="email"
                          placeholder="Enter email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={(e) => {
                            const trimmed = e.target.value.trim();
                            if (trimmed !== e.target.value) {
                              setFormData({ ...formData, email: trimmed });
                            }
                          }}
                          required
                        />
                      </div>

                      <Row3Col>
                        <ThirdCol>
                          <div className="form-group" style={{ width: '100%', position: 'relative', overflow: 'visible', zIndex: showPasswordTooltip ? 1000 : 1 }}>
                            <label htmlFor="signup-password-step1" style={{ position: 'relative', display: 'block' }}>
                              Password<RequiredStar>*</RequiredStar>
                              <TooltipWrapper
                                onMouseEnter={handleTooltipEnter}
                                onMouseLeave={handleTooltipLeave}
                              >
                                <InfoIcon
                                  ref={infoIconRef}
                                  onFocus={() => {
                                    if (infoIconRef.current) {
                                      const rect = infoIconRef.current.getBoundingClientRect();
                                      setTooltipPosition({
                                        top: rect.top - 8,
                                        left: rect.left - 160,
                                      });
                                    }
                                    setShowPasswordTooltip(true);
                                  }}
                                  onBlur={() => setShowPasswordTooltip(false)}
                                  title="Password Requirements"
                                  tabIndex={0}
                                >
                                  i
                                </InfoIcon>
                                {showPasswordTooltip && typeof document !== 'undefined' && createPortal(
                                  <PasswordTooltip
                                    style={{
                                      top: `${tooltipPosition.top}px`,
                                      left: `${tooltipPosition.left}px`,
                                      transform: 'translateY(-100%)',
                                    }}
                                    onMouseEnter={handleTooltipEnter}
                                    onMouseLeave={handleTooltipLeave}
                                  >
                                    <h6>Password Rules & Regulations</h6>
                                    <ul>
                                      <li>Minimum Length: 8 characters</li>
                                      <li>Maximum Length: 20 characters</li>
                                      <li>At least one uppercase letter (A-Z)</li>
                                      <li>At least one lowercase letter (a-z)</li>
                                      <li>At least one number (0-9)</li>
                                      <li>At least one special character (!@#$%^&*)</li>
                                      <li>No spaces allowed</li>
                                    </ul>
                                  </PasswordTooltip>,
                                  document.body
                                )}
                              </TooltipWrapper>
                            </label>
                            <PasswordInputWrapper>
                              <input
                                type={showPass ? "password" : "text"}
                                className="form-control"
                                id="signup-password-step1"
                                name="password"
                                placeholder="Enter password"
                                value={formData.password}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  const trimmed = e.target.value.trim();
                                  if (trimmed !== e.target.value) {
                                    setFormData({ ...formData, password: trimmed });
                                  }
                                }}
                                required
                                style={{ paddingRight: "40px" }}
                              />
                              <button
                                type="button"
                                className="password-toggle"
                                onClick={togglePassword}
                                tabIndex="-1"
                              >
                                <i className={showPass ? "fas fa-eye" : "fas fa-eye-slash"}></i>
                              </button>
                            </PasswordInputWrapper>
                            {passwordErrors.length > 0 && (
                              <ErrorMessage>
                                <i className="fas fa-exclamation-circle"></i>
                                <span>{passwordErrors[0]}</span>
                              </ErrorMessage>
                            )}
                            {formData.password && passwordErrors.length === 0 && (
                              <SuccessMessage>
                                <i className="fas fa-check-circle"></i>
                                <span>Password meets all requirements</span>
                              </SuccessMessage>
                            )}
                          </div>
                        </ThirdCol>
                        <ThirdCol>
                          <div className="form-group" style={{ width: '100%' }}>
                            <label htmlFor="signup-confirm-password-step1">Confirm Password<RequiredStar>*</RequiredStar></label>
                            <PasswordInputWrapper>
                              <input
                                type={showConfirmPass ? "password" : "text"}
                                className="form-control"
                                id="signup-confirm-password-step1"
                                name="confirmPassword"
                                placeholder="Confirm password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  const trimmed = e.target.value.trim();
                                  if (trimmed !== e.target.value) {
                                    setFormData({ ...formData, confirmPassword: trimmed });
                                  }
                                }}
                                required
                                style={{ paddingRight: "40px" }}
                              />
                              <button
                                type="button"
                                className="password-toggle"
                                onClick={toggleConfirmPassword}
                                tabIndex="-1"
                              >
                                <i className={showConfirmPass ? "fas fa-eye" : "fas fa-eye-slash"}></i>
                              </button>
                            </PasswordInputWrapper>
                            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                              <ErrorMessage>
                                <i className="fas fa-exclamation-circle"></i>
                                <span>Passwords do not match</span>
                              </ErrorMessage>
                            )}
                            {formData.confirmPassword && formData.password === formData.confirmPassword && (
                              <SuccessMessage>
                                <i className="fas fa-check-circle"></i>
                                <span>Passwords match</span>
                              </SuccessMessage>
                            )}
                          </div>
                        </ThirdCol>
                      </Row3Col>

                      {error && <ErrorMessage style={{ marginBottom: "1rem" }}><i className="fas fa-exclamation-circle"></i> {error}</ErrorMessage>}

                      <SubmitButton type="submit" disabled={isSubmitting || passwordErrors.length > 0}>
                        {isSubmitting ? "Processing..." : "Start Tender Access"}
                      </SubmitButton>
                    </>
                  ) : (
                    // Step 2: City, State, Zone (from /utils/auto-suggest/all-states-and-zones/), Requirements
                    <>
                      <Row3Col>
                        <ThirdCol>
                          <div className="form-group" style={{ width: '100%' }}>
                            <label htmlFor="signup-city">City<RequiredStar>*</RequiredStar></label>
                            <div style={{ width: '100%' }}>
                              <Select
                                inputId="signup-city"
                                options={citiesOptions}
                                placeholder="Type to search or select city"
                                isSearchable={true}
                                isClearable={false}
                                openMenuOnFocus={true}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                                maxMenuHeight={200}
                                value={
                                  formData.city
                                    ? citiesOptions.find((c) => c.label === formData.city)
                                    : null
                                }
                                onChange={(selectedOption) => {
                                  const nextCity = selectedOption
                                    ? String(
                                        selectedOption.label || selectedOption.value || '',
                                      ).trim()
                                    : '';
                                  if (!nextCity) {
                                    locationFetchIdRef.current += 1;
                                    setCityLocationMatches([]);
                                    setCityLocationLoading(false);
                                    setFormData((prev) => ({
                                      ...prev,
                                      city: '',
                                      state: '',
                                      zone: '',
                                    }));
                                    return;
                                  }
                                  void resolveLocationForCity(nextCity);
                                }}
                                styles={signupStep2LocationSelectStyles}
                              />
                            </div>
                          </div>
                        </ThirdCol>
                        <ThirdCol>
                          <div className="form-group" style={{ width: '100%' }}>
                            <label htmlFor="signup-state">State<RequiredStar>*</RequiredStar></label>
                            <div style={{ width: '100%' }}>
                              {cityLocationLoading ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  disabled
                                  placeholder="Loading..."
                                  value=""
                                  readOnly
                                />
                              ) : !formData.city ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  disabled
                                  placeholder="Select city first"
                                  value=""
                                  readOnly
                                />
                              ) : cityLocationMatches.length === 0 ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  disabled
                                  placeholder="No match for this city"
                                  value=""
                                  readOnly
                                />
                              ) : cityLocationMatches.length === 1 ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  readOnly
                                  value={formData.state}
                                />
                              ) : (
                                <Select
                                  inputId="signup-state"
                                  options={stateOptionsFromCityMatches}
                                  placeholder="Type to search or select state"
                                  isSearchable={true}
                                  isClearable={false}
                                  openMenuOnFocus={true}
                                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                  menuPosition="fixed"
                                  maxMenuHeight={200}
                                  value={
                                    formData.state
                                      ? stateOptionsFromCityMatches.find(
                                          (s) => s.label === formData.state,
                                        ) || null
                                      : null
                                  }
                                  onChange={(selectedOption) => {
                                    const nextState = selectedOption ? selectedOption.label : '';
                                    const zonesForState = [
                                      ...new Set(
                                        cityLocationMatches
                                          .filter((m) => m.state === nextState)
                                          .map((m) => m.zone),
                                      ),
                                    ];
                                    const nextZone =
                                      zonesForState.length === 1 ? zonesForState[0] : '';
                                    setFormData({
                                      ...formData,
                                      state: nextState,
                                      zone: nextZone,
                                    });
                                  }}
                                  styles={signupStep2LocationSelectStyles}
                                />
                              )}
                            </div>
                          </div>
                        </ThirdCol>
                        <ThirdCol>
                          <div className="form-group" style={{ width: '100%' }}>
                            <label htmlFor="signup-zone">Zone<RequiredStar>*</RequiredStar></label>
                            <div style={{ width: '100%' }}>
                              {cityLocationLoading ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  disabled
                                  placeholder="Loading..."
                                  value=""
                                  readOnly
                                />
                              ) : !formData.city ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  disabled
                                  placeholder="Select city first"
                                  value=""
                                  readOnly
                                />
                              ) : cityLocationMatches.length === 0 ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  disabled
                                  placeholder="—"
                                  value=""
                                  readOnly
                                />
                              ) : cityLocationMatches.length === 1 ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  readOnly
                                  value={formData.zone}
                                />
                              ) : (
                                <Select
                                  inputId="signup-zone"
                                  options={zoneOptionsForSelectedState}
                                  placeholder={
                                    formData.state
                                      ? 'Type to search or select zone'
                                      : 'Select state first'
                                  }
                                  isSearchable={true}
                                  isClearable={false}
                                  openMenuOnFocus={true}
                                  isDisabled={!formData.state}
                                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                  menuPosition="fixed"
                                  maxMenuHeight={200}
                                  value={
                                    formData.zone
                                      ? zoneOptionsForSelectedState.find(
                                          (z) => z.label === formData.zone,
                                        ) || null
                                      : null
                                  }
                                  onChange={(selectedOption) => {
                                    setFormData({
                                      ...formData,
                                      zone: selectedOption ? selectedOption.label : '',
                                    });
                                  }}
                                  styles={signupStep2LocationSelectStyles}
                                />
                              )}
                            </div>
                          </div>
                        </ThirdCol>
                      </Row3Col>

                      <div className="form-group">
                        <label htmlFor="signup-requirements">
                          Requirements For Tender<RequiredStar>*</RequiredStar>
                        </label>
                        <RequirementsInputWrapper>
                          <textarea
                            className="form-control"
                            id="signup-requirements"
                            name="requirements"
                            rows={4}
                            placeholder="Type to see suggestions or enter manually..."
                            value={requirementsInputValue || formData.requirements}
                            onChange={handleRequirementsChange}
                            onFocus={() => {
                              if (requirementsInputValue.trim().length >= 2) {
                                setShowRequirementsSuggestions(true);
                              }
                            }}
                            onBlur={(e) => {
                              // Trim the value
                              const trimmed = e.target.value.trim();
                              if (trimmed !== e.target.value) {
                                setFormData({ ...formData, requirements: trimmed });
                                setRequirementsInputValue(trimmed);
                              }
                              // Delay to allow clicking on suggestions
                              setTimeout(() => {
                                setShowRequirementsSuggestions(false);
                              }, 200);
                            }}
                            required
                          />
                          {showRequirementsSuggestions && requirementsSuggestions.length > 0 && (
                            <div className="suggestions-dropdown">
                              {requirementsSuggestions.map((suggestion, index) => (
                                <div
                                  key={index}
                                  className="suggestion-item"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSuggestionSelect(suggestion);
                                  }}
                                >
                                  {suggestion}
                                </div>
                              ))}
                            </div>
                          )}
                        </RequirementsInputWrapper>
                      </div>

                      {error && <ErrorMessage style={{ marginBottom: "1rem" }}><i className="fas fa-exclamation-circle"></i> {error}</ErrorMessage>}

                      <StepNavigation>
                        <SubmitButton
                          type="submit"
                          disabled={isSubmitting || !isStep2Complete()}
                          title={
                            !isStep2Complete()
                              ? "Please fill City, State, Zone, and Requirements"
                              : undefined
                          }
                          style={{ marginLeft: 'auto' }}
                        >
                          {isSubmitting ? "Signing Up..." : "Sign Up"}
                        </SubmitButton>
                      </StepNavigation>
                    </>
                  )}

                  {signupStep === 1 && (
                    <LinkText>
                      <span>Already have an account? </span>
                      <a onClick={switchToLogin} className="px-2">
                        Login
                      </a>
                    </LinkText>
                  )}
                </form>
              </FormBody>
            </>
          )}
        </FormContainer>
      </Modal.Body>
    </ModalStyled>
  );
};

export default ModalAuth;
